import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/use-toast";
import { Plus, Search, Edit2, Trash2, Play, Loader2, Clock, CheckCircle, XCircle, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../supabase/auth";
import { supabase } from "../../../supabase/supabase";
import { useOrganization } from "../../App";
import SimulatorForm from "./SimulatorForm";
import SimulatorTranscript from "./SimulatorTranscript";
import { simulationService } from "@/services/simulationService";

type Simulator = {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  persona_id: string;
  assistant_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  transcript: any[];
  configuration: any;
  started_at: string | null;
  ended_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string | null;
  // Joined data
  personas?: {
    name: string;
  };
  assistants?: {
    name: string;
  };
};

interface SimulatorsListProps {
  onSimulatorSelect?: (id: string) => void;
  selectedSimulatorId?: string | null;
  className?: string | null;
}

export default function SimulatorsList({
  onSimulatorSelect,
  selectedSimulatorId: propSelectedSimulatorId,
  className = "",
}: SimulatorsListProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const [simulators, setSimulators] = useState<Simulator[]>([]);
  const [filteredSimulators, setFilteredSimulators] = useState<Simulator[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(
    propSelectedSimulatorId || null
  );
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingSimulator, setEditingSimulator] = useState<Simulator | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingSimulator, setDeletingSimulator] = useState<Simulator | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showTranscriptDialog, setShowTranscriptDialog] = useState(false);
  const [viewingSimulator, setViewingSimulator] = useState<Simulator | null>(null);
  const [runningSimulations, setRunningSimulations] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (user && currentOrganization) {
      fetchSimulators();
    }
  }, [user, currentOrganization]);

  useEffect(() => {
    if (!user || !currentOrganization) return;

    // Set up real-time subscription
    const subscription = supabase
      .channel(`simulators-changes-${currentOrganization.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "simulators",
          filter: `organization_id=eq.${currentOrganization.id}`,
        },
        () => fetchSimulators()
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user, currentOrganization]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredSimulators(simulators);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredSimulators(
        simulators.filter(
          (simulator) =>
            simulator.name.toLowerCase().includes(query) ||
            (simulator.description &&
              simulator.description.toLowerCase().includes(query)) ||
            (simulator.personas?.name.toLowerCase().includes(query)) ||
            (simulator.assistants?.name.toLowerCase().includes(query))
        )
      );
    }
  }, [searchQuery, simulators]);

  useEffect(() => {
    setSelectedId(propSelectedSimulatorId || null);
  }, [propSelectedSimulatorId]);

  const fetchSimulators = async () => {
    setIsLoading(true);
    try {
      if (!currentOrganization) {
        setSimulators([]);
        setFilteredSimulators([]);
        setIsLoading(false);
        return;
      }

      const { data: simulatorsData, error: simulatorsError } = await supabase
        .from("simulators")
        .select(`
          *,
          personas:persona_id (name),
          assistants:assistant_id (name)
        `)
        .eq("organization_id", currentOrganization.id)
        .order("created_at", { ascending: false });

      if (simulatorsError) {
        console.error("Error fetching simulators:", simulatorsError);
        toast({
          title: "Error",
          description: "Failed to load simulators. Please try again.",
          variant: "destructive",
        });
      } else if (simulatorsData) {
        setSimulators(simulatorsData);
        setFilteredSimulators(simulatorsData);
      }
    } catch (error) {
      console.error("Error fetching simulators:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while loading simulators.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSimulator = () => {
    setShowCreateDialog(true);
  };

  const handleEditSimulator = (simulator: Simulator) => {
    setEditingSimulator(simulator);
    setShowEditDialog(true);
    if (onSimulatorSelect) {
      onSimulatorSelect(simulator.id);
    }
    setSelectedId(simulator.id);
  };

  const handleDeleteSimulator = (simulator: Simulator) => {
    setDeletingSimulator(simulator);
    setShowDeleteDialog(true);
  };

  const confirmDeleteSimulator = async () => {
    if (!deletingSimulator) return;

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from("simulators")
        .delete()
        .eq("id", deletingSimulator.id);

      if (error) throw error;

      toast({
        title: "Simulator Deleted",
        description: "The simulator has been deleted successfully.",
      });

      setShowDeleteDialog(false);
      setDeletingSimulator(null);
      fetchSimulators();
    } catch (error: any) {
      console.error("Error deleting simulator:", error);
      toast({
        title: "Deletion Failed",
        description: error.message || "Failed to delete the simulator.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFormSuccess = () => {
    setShowCreateDialog(false);
    setShowEditDialog(false);
    setEditingSimulator(null);
    fetchSimulators();
  };

  const handleRunSimulation = async (simulator: Simulator) => {
    try {
      // Debug: Log simulator data
      console.log('Simulator data:', simulator);
      console.log('Organization ID:', simulator.organization_id);
      
      // Validate that organization_id exists
      if (!simulator.organization_id) {
        toast({
          title: "Error",
          description: "Missing organization ID for simulator. Please refresh and try again.",
          variant: "destructive",
        });
        return;
      }
      
      // Add to running set
      setRunningSimulations(prev => new Set(prev).add(simulator.id));

      // Call API to run simulation
      const response = await simulationService.runSimulation(simulator.id, simulator.organization_id);
      
      toast({
        title: "Simulation Started",
        description: `Simulation "${simulator.name}" has been started.`,
      });

      // Start polling for status updates
      simulationService.pollSimulationStatus(
        simulator.id,
        simulator.organization_id,
        (status) => {
          // Update the simulator in the list with new status
          setSimulators(prev => prev.map(s => 
            s.id === simulator.id 
              ? { ...s, status: status.status as any, transcript: status.transcript }
              : s
          ));
          setFilteredSimulators(prev => prev.map(s => 
            s.id === simulator.id 
              ? { ...s, status: status.status as any, transcript: status.transcript }
              : s
          ));

          // If completed or failed, remove from running set
          if (status.status === 'completed' || status.status === 'failed') {
            setRunningSimulations(prev => {
              const newSet = new Set(prev);
              newSet.delete(simulator.id);
              return newSet;
            });

            // Show completion toast
            toast({
              title: status.status === 'completed' ? "Simulation Completed" : "Simulation Failed",
              description: `Simulation "${simulator.name}" has ${status.status}.`,
              variant: status.status === 'failed' ? "destructive" : "default",
            });

            // Refresh simulators to get latest data
            fetchSimulators();
          }
        },
        2000, // Poll every 2 seconds
        150 // Max 5 minutes
      ).catch(error => {
        console.error("Error polling simulation:", error);
        setRunningSimulations(prev => {
          const newSet = new Set(prev);
          newSet.delete(simulator.id);
          return newSet;
        });
      });

    } catch (error: any) {
      console.error("Error running simulation:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to start simulation",
        variant: "destructive",
      });
      
      // Remove from running set
      setRunningSimulations(prev => {
        const newSet = new Set(prev);
        newSet.delete(simulator.id);
        return newSet;
      });
    }
  };

  const handleViewTranscript = (simulator: Simulator) => {
    setViewingSimulator(simulator);
    setShowTranscriptDialog(true);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-muted-foreground" />;
      case 'running':
        return <Play className="h-4 w-4 text-foreground" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-good" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-danger" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-muted text-foreground';
      case 'running':
        return 'bg-secondary text-foreground';
      case 'completed':
        return 'bg-good-bg text-good';
      case 'failed':
        return 'bg-danger-bg text-danger';
      default:
        return 'bg-muted text-foreground';
    }
  };

  const formatDuration = (startedAt: string | null, endedAt: string | null) => {
    if (!startedAt) return null;
    
    const start = new Date(startedAt);
    const end = endedAt ? new Date(endedAt) : new Date();
    const durationMs = end.getTime() - start.getTime();
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  return (
    <div className={`${className} h-full flex flex-col`}>
      <div className="pr-6 py-6 space-y-4">
        <div className="flex items-center space-x-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search simulators..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-background border-border"
            />
          </div>
          <Button
            onClick={handleCreateSimulator}
            className="bg-primary hover:bg-primary/80 text-primary-foreground font-medium"
          >
            <Plus className="mr-2 h-4 w-4" />
            Create Simulator
          </Button>
        </div>

        <Separator />

        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {/* 1 col mobile / 2 tablet / 3 desktop / 4 large desktop */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredSimulators.map((simulator) => (
              <Card
                key={simulator.id}
                className={`bg-card border-border hover:border-primary transition-colors cursor-pointer ${
                  selectedId === simulator.id ? "border-primary" : ""
                }`}
                onClick={() => handleEditSimulator(simulator)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="font-medium text-base text-primary break-words">
                          {simulator.name}
                        </h3>
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(simulator.status)}`}
                        >
                          {getStatusIcon(simulator.status)}
                          {simulator.status.charAt(0).toUpperCase() + simulator.status.slice(1)}
                        </span>
                      </div>
                      
                      {simulator.description && (
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                          {simulator.description}
                        </p>
                      )}
                      
                      <div className="text-sm text-muted-foreground space-y-1">
                        <div className="flex items-center gap-4">
                          <span>👤 {simulator.personas?.name || 'Unknown Persona'}</span>
                          <span>🤖 {simulator.assistants?.name || 'Unknown Assistant'}</span>
                        </div>
                        
                        {simulator.started_at && (
                          <div className="flex items-center gap-4">
                            <span>⏱️ {formatDuration(simulator.started_at, simulator.ended_at)}</span>
                            <span>💬 {simulator.transcript?.length || 0} messages</span>
                          </div>
                        )}
                        
                        <div className="text-xs text-muted-foreground/70">
                          Created {new Date(simulator.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {/* Run Simulation Button */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRunSimulation(simulator);
                        }}
                        disabled={simulator.status === 'running' || runningSimulations.has(simulator.id)}
                        className="text-primary hover:bg-primary hover:text-primary-foreground"
                      >
                        {runningSimulations.has(simulator.id) || simulator.status === 'running' ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </Button>
                      
                      {/* View Transcript Button */}
                      {simulator.transcript && simulator.transcript.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewTranscript(simulator);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditSimulator(simulator);
                        }}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSimulator(simulator);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            </div>

            {simulators.length === 0 && (
              <div className="text-center py-12">
                <Play className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-base font-medium mb-2">
                  No simulators found
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Create your first simulator to start testing with personas and agents
                </p>
                <Button
                  onClick={handleCreateSimulator}
                  className="bg-primary hover:bg-primary/80 text-primary-foreground font-medium text-sm w-full"
                >
                  Create First Simulator
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Simulator Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Simulator</DialogTitle>
            <DialogDescription>
              Create a simulation session by selecting a persona and agent to interact.
            </DialogDescription>
          </DialogHeader>
          <SimulatorForm 
            simulator={null}
            onSuccess={handleFormSuccess}
            onCancel={() => setShowCreateDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Simulator Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Simulator</DialogTitle>
            <DialogDescription>
              Update the simulator configuration and settings.
            </DialogDescription>
          </DialogHeader>
          <SimulatorForm 
            simulator={editingSimulator}
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setShowEditDialog(false);
              setEditingSimulator(null);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Simulator</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingSimulator?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setDeletingSimulator(null);
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteSimulator}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transcript Viewer Dialog */}
      {viewingSimulator && (
        <SimulatorTranscript
          simulator={viewingSimulator}
          open={showTranscriptDialog}
          onClose={() => {
            setShowTranscriptDialog(false);
            setViewingSimulator(null);
          }}
        />
      )}
    </div>
  );
}